import React, { FC, useCallback, useState } from 'react';
import { useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Link } from '@mui/material';

import { useAuthApp } from 'hooks/useAuthApp';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { ThunkLogin } from 'store/auth/thunks';
import { ILoginResponse } from 'services/auth/types';
import { RootState } from 'store/types';

import { HOME } from 'router/routes';
import { getPostLoginRedirectPath } from 'utils/postLoginRedirect';
import { LOCAL_STORAGE_REFRESH_TOKEN, LOCAL_STORAGE_TOKEN } from 'constants/storage';

import Input from 'components/Input/Input';
import Loader from 'components/Loader';
import ForgotPasswordDialog from './dialogs/ForgotPasswordDialog';

import { ILoginFormInitialValues, loginFormValidationSchema, loginInitialValues } from './form';
import { tt } from '@i18n/tt';

import useStyles from './styles';

const Login: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectAfterLogin = getPostLoginRedirectPath(
    (location.state as { from?: { pathname: string; search: string } } | null)?.from,
    HOME,
  );
  const classes = useStyles();
  const config = useAppSelector((state: RootState) => state.config.config);

  const { handleExternalLogin } = useAuthApp(setLoading);

  const handleSubmit = useCallback(
    async (values: ILoginFormInitialValues) => {
      setLoading(true);

      const payload: { payload: ILoginFormInitialValues } = {
        payload: values,
      };
      await dispatch(ThunkLogin(payload))
        .unwrap()
        .then((res: ILoginResponse) => {
          localStorage.setItem(LOCAL_STORAGE_TOKEN, res.access_token);
          localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, res.refresh_token);
          navigate(redirectAfterLogin, { replace: true });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [dispatch, navigate, redirectAfterLogin],
  );

  const formik = useFormik({
    initialValues: loginInitialValues,
    validationSchema: loginFormValidationSchema,
    onSubmit: handleSubmit,
  });

  const onForgotPasswordClick = (): void => {
    setForgotPasswordDialogOpen(true);
  };

  if (loading) return <Loader />;
  return (
    <div className={classes.formWrapper}>
      <form onSubmit={formik.handleSubmit} className={classes.form}>
        {config.logo && <img src={config.logo} alt="logo" className={classes.logo} />}
        <Input label={tt('fields.email')} name="email" formik={formik} />
        <Input label={tt('fields.password')} name="password" type="password" formik={formik} />
        <Button type="submit" variant="contained"
        >{tt('pages.login.login')}</Button>
        <Link onClick={onForgotPasswordClick} className={classes.link}>
          {tt('pages.login.forgotPassword')}
        </Link>

        <div className={classes.externalLoginContainer}>
          <Button
            variant="contained"
            type="button"
            onClick={() => {
              handleExternalLogin('Microsoft');
            }}
            fullWidth
          >
            Microsoft
          </Button>
          <Button
            variant="contained"
            type="button"
            onClick={() => {
              handleExternalLogin('Google');
            }}
            fullWidth
          >
            Google
          </Button>
        </div>
      </form>
      <ForgotPasswordDialog
        open={forgotPasswordDialogOpen}
        onClose={() => {
          setForgotPasswordDialogOpen(false);
        }}
      />
    </div>
  );
};

export default Login;
